const REDACT_CSS_CLASS = 'data-redact'
const REDACT_DATA_ATTR = 'data-redact'
const REDACT_HEAP_ATTR = 'data-heap-redact-text'
const REDACT_HEAP_ATTRIBUTES_ATTR = 'data-heap-redact-attributes'
const REDACT_CONTENTSQUARE_ATTR = 'data-cs-mask'
const REDACT_APPZI_ATTR = 'appzi-screenshot-exclude'

/**
 * Attributes stripped from media elements before the DOM snapshot leaves the
 * browser. `src`/`srcset`/`href` carry the presigned S3 URL for uploaded files,
 * which embeds live AWS STS credentials (X-Amz-Credential, X-Amz-Security-Token,
 * X-Amz-Signature); `alt`/`title`/`aria-label` carry the uploaded file name,
 * which is often an address. Heap takes a comma-separated list.
 */
const REDACTED_MEDIA_ATTRIBUTES = 'src,srcset,href,alt,title,aria-label'

export const redactClassNames = [REDACT_CSS_CLASS]
export const redactClassName = redactClassNames.join(' ')

/**
 * Use this when you have other className values to add to the element.
 * @example
 * <Text className={redactClassName} {...redactProps}>PII Value</Text>
 */
export const redactProps = {
  [REDACT_DATA_ATTR]: 'true',
  [REDACT_HEAP_ATTR]: 'true',
  [REDACT_APPZI_ATTR]: 'true',
}

/**
 * Use this when you don't have another className to add to the element. Keeps it simple
 * @example
 * <Text {...redactPropsWithClassName}>PII Value</Text>
 */
export const redactPropsWithClassName = {
  ...redactProps,
  className: redactClassName,
}

/**
 * Redaction for media elements (<img>, <iframe>, <video>) whose PII is the
 * resource itself rather than any text it contains — uploaded property photos
 * and documents, for example.
 *
 * `redactProps` only covers text, so it leaves an image fully intact in a
 * session replay. This adds:
 * - `data-cs-mask`, which removes the element's content in the browser before
 *   it is sent to Contentsquare. It is NOT inherited, so it has to sit on the
 *   media element itself, not a wrapper.
 * - `data-heap-redact-attributes`, which strips the presigned URL and file name
 *   before they leave the browser. This one does cascade to descendants.
 *
 * Note this governs what the replay captures. It does not stop Contentsquare's
 * static resource scraper from fetching a URL it already collected, so it is
 * not a substitute for serving these files from an authenticated route.
 *
 * @example
 * <Image src={photo.presigned_url} alt={photo.file_name} {...redactMediaProps} />
 */
export const redactMediaProps = {
  [REDACT_DATA_ATTR]: 'true',
  [REDACT_HEAP_ATTRIBUTES_ATTR]: REDACTED_MEDIA_ATTRIBUTES,
  [REDACT_CONTENTSQUARE_ATTR]: 'true',
  [REDACT_APPZI_ATTR]: 'true',
}

/**
 * Use this when you don't have another className to add to the media element.
 * @example
 * <Image src={photo.presigned_url} alt={photo.file_name} {...redactMediaPropsWithClassName} />
 */
export const redactMediaPropsWithClassName = {
  ...redactMediaProps,
  className: redactClassName,
}

/**
 * Redact named attributes on an element whose rendered appearance is safe but
 * whose attributes carry PII — an `aria-label` built from an uploaded file
 * name, for example.
 *
 * Unlike `redactMediaProps` this does not mask the element, so a control still
 * appears in the replay and only its attribute values are stripped.
 *
 * @example
 * <ActionIcon {...redactAttributes('aria-label')} aria-label={`Delete ${file.file_name}`} />
 */
export function redactAttributes(...attributes: string[]) {
  return {
    [REDACT_DATA_ATTR]: 'true',
    [REDACT_HEAP_ATTRIBUTES_ATTR]: attributes.join(','),
  }
}
